import { IfWrites } from '../write-access';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  sameEntityId,
  type Fact,
  type KnowledgeEntry,
  type KnowledgePanel,
  type SubjectStatus,
} from '@ni/shared';
import {
  FACT_TYPE_LABELS,
  KNOWLEDGE_FACTS_HEAD,
  KNOWLEDGE_GRAPH_HEAD,
  KNOWLEDGE_JOIN_ACTION,
  KNOWLEDGE_KIND_WORDS,
  KNOWLEDGE_LINK_ACTION,
  KNOWLEDGE_NO_FACTS,
  KNOWLEDGE_NO_TOUCHES,
  KNOWLEDGE_OPEN_RECORD,
  KNOWLEDGE_RENAME_ACTION,
  KNOWLEDGE_PANEL_CLOSE,
  KNOWLEDGE_PROVENANCE_HEAD,
  KNOWLEDGE_TOUCHES_HEAD,
  NEGATION_LABELS,
  ORGANISATION_TRACK_ACTION,
  ORGANISATION_TRACK_WORKING,
  knowledgeConnections,
  knowledgeFactSubject,
  knowledgeFactsCapped,
  knowledgeOpenLabel,
  knowledgeFactCount,
  knowledgeSourceCount,
  knowledgeStanding,
  knowledgeUpdated,
  subjectStatusLabel,
  KNOWN_LEVEL_LABEL,
  PEOPLE_LEVEL_LABELS,
} from '../../lib/copy';
import { corroborationClause, factDating, factLine } from '@ni/shared';
import { joinClauses } from '../../lib/format';
import { factGroups, provenanceOn, touchGroups } from '../../lib/knowledge';
import { buildGraphModel } from '../../lib/network-graph';
import { Graph } from '../../routes/person/network';
import { ProvenanceList } from './provenance-list';
import { TrackDoor } from '../people/track-door';
import { ErrorLine } from '../ui/error-line';
import { OverflowMenu } from '../ui/overflow-menu';
import { EmptyLine } from '../ui/empty-line';
import { StatusWord } from '../ui/status-word';
import './knowledge.css';
import './panel-graph.css';

/**
 * **The panel** (design note 2026-09-07 §1.4, revised): everything the system
 * knows about one person, organisation or event — the head, the graph, who it
 * touches, what we know, where it came from.
 *
 * The graph leads. It was third, which meant the picture of the network sat
 * under two lists and was reached by scrolling; the whole reason a reader opens
 * this column is to see who somebody touches, and the drawing says that faster
 * than the list of names under it does.
 *
 * It carries **four gestures and no more**: Track, on a record the office does
 * not yet keep one of — a known person, with the vertical's words, or an
 * organisation, with one press and no words at all, since an organisation has no
 * status; **Link to another record** on an organisation or an event
 * (organisation-profile note §1.1); **Rename**, on the same two
 * (organisations-as-subjects note §2 chunk 1); and **Join with another record**
 * on a person, which is the one door here a person's entry carries and the one
 * exception to the rule that a person's name is only changed on their own page
 * (same note, "Join by hand"). Beside them, on a record that already exists, is
 * the way into it.
 *
 * **Where they sit changed, and not which they are.** Written out along the
 * head's one line they overflowed the column — cut off mid-word at the panel's
 * edge — and squeezed the name until its standing line broke a word to a line.
 * So the name and its line take the width, and every single-press action folds
 * behind one "…" at the right. The person's Track is the exception, because it
 * is not a press: it opens a picker of its own, and a picker inside a menu has
 * two lids and one Escape.
 *
 * What is left of §1.7 is no delete and no "research this organisation".
 * A calm surface is still the point — a reader is here to decide something about
 * somebody, not to administer records — and each of the three earns its place by
 * being something only the office can say.
 *
 * Nothing here fetches. The route owns the read and hands the answer down, which
 * is what lets the whole panel be asserted as static markup.
 */
export function KnowledgePanelView({
  panel,
  vertical,
  onClose,
  onSelect,
  track,
  trackOrganisation,
  onLink,
  onRename,
  onJoin,
  recorded = null,
}: {
  panel: KnowledgePanel;
  /** The reader's own org's vertical — the words a status is said in. */
  vertical: string;
  onClose: () => void;
  /** Hopping: a row under "Who it touches" opens that one's panel instead. */
  onSelect: (entityId: string) => void;
  /** The adopt door's state, or null where there is nothing to adopt. */
  track: PanelTrack | null;
  /**
   * **The organisation's own Track**, or null where there is none — a person's
   * panel, an event's, and an organisation the office already keeps a record of.
   *
   * A second prop rather than a mode of `track` because it is a different door:
   * one press against a picker, no vocabulary, and a different write. Which
   * panels are offered which is the route's rule, asserted where it is made.
   */
  trackOrganisation: PanelTrackOrganisation | null;
  /**
   * Opens the link modal, or null where there is nothing this record can be
   * linked to — a person, and anywhere the route is not offering the door.
   *
   * The modal itself belongs to the route, like both of this panel's reads: it
   * searches and it writes, and the panel is a component that does neither.
   */
  onLink: (() => void) | null;
  /**
   * Opens the rename modal, or null where the route is not offering the door.
   *
   * Offered for an organisation and an event and **not for a person here**, and
   * that is a decision about *this surface* rather than about the write: the
   * endpoint is kind-agnostic (organisations-as-subjects note R6, and one of the
   * four early merges that kept the shorter spelling is a person), but a tracked
   * person has a record page of their own whose header is where their name is
   * changed. Two doors to one name on two different screens is how a reader
   * comes to wonder which of them is the real one.
   *
   * **`onJoin` below is the ruled exception to that**, and it is worth saying
   * where the rule is stated: a person's entry *does* carry Join, tracked or
   * not. The reasoning above does not reach it — the record that needs folding
   * away is by definition the one with no page, a bare "Clint" off an import,
   * and there is nowhere else on this product to stand while looking at it.
   */
  onRename: (() => void) | null;
  /**
   * Opens the join modal, or null where the route is not offering the door — an
   * organisation and an event, whose duplicates arrive as a likely-merge card
   * with a question already attached to them.
   *
   * **A person's entry carries it whether or not the office tracks them**
   * (organisations-as-subjects note §2 chunk 1, "Join by hand", owner's call).
   * An untracked one is the common half of the pair; a tracked one is the record
   * that survives, and offering the door only on the loser's entry would mean a
   * reader standing on the real person could not start the gesture from there.
   */
  onJoin: (() => void) | null;
  /**
   * **What the last write on this panel did**, in the server's own sentence, or
   * null. The link modal closes on the press, so the sentence it came back with
   * has to stand where the reader is returned to — the same place and the same
   * line the organisation's record page prints it.
   */
  recorded?: string | null;
}) {
  const { entry } = panel;
  const model = useMemo(() => buildGraphModel(panel.network), [panel.network]);
  const touches = touchGroups(panel.touches);
  const groups = factGroups(panel.facts);
  /* **The one gate for the record**, read by all three of the places that would
     point at it: the name, the head's action, and the capped-facts line.

     It was a tracked *person* and nothing else. Since 2026-09-09 an organisation
     the office tracks has a record page of its own (organisations-as-subjects
     note §1 R8), so the gate is `tracked` plus a kind that has a page — and
     `recordHref` is which page, because the two live under different addresses.
     An event has neither, and a known person still has none: there is no record
     until somebody tracks them, which is what the Track door beside this is for. */
  const recordHref = recordAddress(entry);
  const hasRecord = recordHref !== null;
  /* **What is behind the glyph**, in the order it is offered. Counted rather
     than assumed, because on most panels it is one item and on a person's who
     is already tracked it is exactly one — and on an event with no doors it is
     none at all, which is the case the button must not appear for. */
  const items = [
    hasRecord,
    trackOrganisation !== null,
    onLink !== null,
    onRename !== null,
    onJoin !== null,
  ].filter(Boolean);

  return (
    <div className="knowledge-panel">
      <div className="knowledge-panel-head">
        <div className="knowledge-panel-titles">
          <div className="knowledge-panel-name">
            {/* A record's id *is* its entity id for both kinds that have one, so
                the record is reachable from the row itself — no second read to
                find it. */}
            {recordHref !== null ? (
              <Link className="link" to={recordHref}>
                {entry.display_name}
              </Link>
            ) : (
              entry.display_name
            )}
          </div>
          {/* The kind, the standing, and the one date on this surface: the row
              in the list no longer carries it, and this is where a reader who
              has stopped on one thing can use it. */}
          <div className="knowledge-meta">
            {entry.account ? `${entry.account.label} · ` : null}
            {KNOWLEDGE_KIND_WORDS[entry.kind]} ·{' '}
            <KnowledgeStanding entry={entry} vertical={vertical} /> ·{' '}
            {knowledgeUpdated(entry.updated_at)}
          </div>
        </div>

        <div className="knowledge-panel-actions">
          {/* **The person's Track stays on the head**, and is the one action
              that does. It is not a press: it replaces itself with the
              vertical's words and needs three ways back out of that state, so
              inside a menu the reader's way out of the picker would be the way
              out of the menu holding it. Only single-press actions fold. */}
          {track ? (
            <IfWrites>
              <TrackDoor
                vertical={vertical}
                name={entry.display_name}
                entityId={entry.entity_id}
                open={track.open}
                busy={track.busy}
                error={track.error}
                onOpen={track.onOpen}
                onChoose={track.onChoose}
              />
            </IfWrites>
          ) : null}
          <button type="button" className="quiet-action" onClick={onClose}>
            {KNOWLEDGE_PANEL_CLOSE}
          </button>
          {/* **Everything else, behind one glyph.** Written out in a row the
              three actions an organisation carries were wider than the column:
              they were cut off mid-word at the panel's edge, and the name they
              took the width from was squeezed until its standing line broke one
              word to a line. The name is what the reader came for.

              No glyph where there is nothing behind it — a button that opens an
              empty list is a promise of actions this record has none of. */}
          {items.length > 0 ? (
            <IfWrites>
              <OverflowMenu>
                {/* The way into the record leads, as it did on the row: it is
                  where the reader is most often going, and it is the only one
                  of these that is not a write. */}
                {recordHref !== null ? (
                  <Link className="overflow-menu-item" role="menuitem" to={recordHref}>
                    {KNOWLEDGE_OPEN_RECORD}
                  </Link>
                ) : null}
                {/* **The organisation's Track** (organisations-as-subjects note §1
                  R8) — one press and no picker, which is what lets it fold in
                  where the person's door cannot. Its refusal is reported under
                  the head rather than in here, since a reason inside a closed
                  menu is a reason nobody reads. */}
                {trackOrganisation ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="overflow-menu-item"
                    aria-busy={trackOrganisation.busy}
                    disabled={trackOrganisation.busy}
                    onClick={trackOrganisation.onTrack}
                  >
                    {trackOrganisation.busy
                      ? ORGANISATION_TRACK_WORKING
                      : ORGANISATION_TRACK_ACTION}
                  </button>
                ) : null}
                {/* **The one write on the panel** (organisation-profile note §1.1):
                  an organisation or an event links by an `org_relation`, and a
                  person (2026-09-30) links another person by a family tie. */}
                {onLink ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="overflow-menu-item"
                    onClick={onLink}
                  >
                    {KNOWLEDGE_LINK_ACTION}
                  </button>
                ) : null}
                {/* **Under Link, and second to it** (organisations-as-subjects note
                  §2 chunk 1). Link is what this panel is opened to do; renaming
                  is the correction a reader makes on the way past, and the order
                  says which is which. */}
                {onRename ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="overflow-menu-item"
                    onClick={onRename}
                  >
                    {KNOWLEDGE_RENAME_ACTION}
                  </button>
                ) : null}
                {/* **Last, and deliberately** (organisations-as-subjects note §2
                  chunk 1, "Join by hand"). It is the only item behind this glyph
                  with no undo, and the only one that can make the record the
                  panel is open on stop existing. A reader reaching for something
                  else should not pass it on the way. */}
                {onJoin ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="overflow-menu-item"
                    onClick={onJoin}
                  >
                    {KNOWLEDGE_JOIN_ACTION}
                  </button>
                ) : null}
              </OverflowMenu>
            </IfWrites>
          ) : null}
        </div>
      </div>

      {/* A refused track, said where the reader is looking. It cannot stand
          under the button that asked for it any more — that button is inside a
          menu that shuts on the press — so it stands under the head, on the same
          line the link's own sentence uses. */}
      {trackOrganisation?.error ? <ErrorLine error={trackOrganisation.error} /> : null}

      {/* Under the head, once, as on the organisation's record. */}
      {recorded ? <div className="src">{recorded}</div> : null}

      <div className="knowledge-panel-body">
        {/* **The drawing first**: it answers the question this page is opened
            with, who does this touch. Its box is sized in CSS rather than by its
            contents, so the measure taken on mount is the real one.

            `besideDetail` is false because this *is* the narrow column: a
            selected line's detail goes in the app's modal. */}
        <section className="knowledge-sec knowledge-graph">
          <h3>{KNOWLEDGE_GRAPH_HEAD}</h3>
          <Graph model={model} alone={model.links.length === 0} besideDetail={false} />
        </section>

        <section className="knowledge-sec">
          <h3>
            {KNOWLEDGE_TOUCHES_HEAD}
            {panel.touches.length > 0 ? (
              <span className="c num">{panel.touches.length}</span>
            ) : null}
          </h3>
          {panel.touches.length === 0 ? (
            <EmptyLine>{KNOWLEDGE_NO_TOUCHES}</EmptyLine>
          ) : (
            <ul className="knowledge-touches">
              {[...touches.people, ...touches.places].map((touch) => (
                <li key={touch.entity_id}>
                  <TouchRow entry={touch} vertical={vertical} onSelect={onSelect} />
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* What is on record and where it came from, each behind one closed
            disclosure (blend spec, "Depth on demand"). */}
        <details className="knowledge-behind">
          <summary>
            {KNOWLEDGE_FACTS_HEAD}
            <span className="quiet">
              {' '}
              · {knowledgeFactCount(panel.facts.length + (panel.observations?.length ?? 0))}
            </span>
          </summary>
          {groups.length === 0 && !panel.observations?.length ? (
            <EmptyLine>{KNOWLEDGE_NO_FACTS}</EmptyLine>
          ) : (
            <ul className="knowledge-know">
              {groups.map((group) => (
                <li key={group.type}>
                  <span className="k">{FACT_TYPE_LABELS[group.type]}</span>
                  <div className="v">
                    {group.facts.map((fact) => (
                      <PanelFact key={fact.lineage_id} fact={fact} entry={entry} />
                    ))}
                  </div>
                </li>
              ))}
              {panel.observations?.map((item) => (
                <li key={item.id}>
                  <span className="k" />
                  <div className="v">
                    <div className="cat-item">
                      <div className="cat-line">{item.statement}</div>
                      <div className="src">{item.basis}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {panel.facts_capped ? (
            <p className="knowledge-capped">
              {knowledgeFactsCapped(panel.facts.length, hasRecord)}
            </p>
          ) : null}
        </details>

        <details className="knowledge-behind">
          <summary>
            {KNOWLEDGE_PROVENANCE_HEAD}
            <span className="quiet"> · {knowledgeSourceCount(panel.provenance.length)}</span>
          </summary>
          {/* The lines are `ProvenanceList`'s; where they point is the entry's
              (`provenanceOn`): whether the office keeps a record of this entity
              at all first, and only then which kind of record it is. */}
          <ProvenanceList
            provenance={panel.provenance}
            entityId={entry.entity_id}
            on={provenanceOn(entry)}
          />
        </details>
      </div>
    </div>
  );
}

/**
 * **The org's standing with an entry**, as a row and the panel's head say it:
 * "Known", "Tracked", or "Tracked" and the status with its dot. The words are
 * `knowledgeStanding`'s; the dot is the status tone's (blend spec, "Status
 * tones").
 */
export function KnowledgeStanding({
  entry,
  vertical,
}: {
  entry: { tracked: { status: SubjectStatus | null } | null };
  vertical: string;
}) {
  if (!entry.tracked) return <>{KNOWN_LEVEL_LABEL}</>;
  const status = entry.tracked.status;
  if (status === null || subjectStatusLabel(vertical, status) === null) {
    return <>{knowledgeStanding(entry, vertical)}</>;
  }
  return (
    <>
      {PEOPLE_LEVEL_LABELS.tracked} · <StatusWord vertical={vertical} status={status} />
    </>
  );
}

/**
 * **Where this record's own page is**, or null when it has none
 * (organisations-as-subjects note §1 R8).
 *
 * Both kinds that have a page are addressed by the entity id, which is also the
 * record row's key — there is no second id to fetch. An event has no page, and
 * neither has anything the office merely knows of.
 *
 * Exported so the route's spec can name the same address this draws without
 * restating it as a string literal in two places.
 */
export function recordAddress(entry: KnowledgeEntry): string | null {
  if (entry.tracked === null) return null;
  if (entry.kind === 'person') return `/people/${entry.entity_id}`;
  if (entry.kind === 'org') return `/organizations/${entry.entity_id}`;
  if (entry.kind === 'event') return `/events/${entry.entity_id}`;
  return null;
}

/** Everything the panel's own adopt door needs, owned by the route that writes. */
export interface PanelTrack {
  open: boolean;
  busy: boolean;
  error: unknown;
  onOpen: (entityId: string | null) => void;
  onChoose: (status: SubjectStatus) => void;
}

/**
 * The organisation door's whole state: it writes or it does not, and there is
 * nothing to open, because one press is the whole gesture.
 */
export interface PanelTrackOrganisation {
  busy: boolean;
  error: unknown;
  onTrack: () => void;
}

/**
 * One row of the hop. It names the same three things the search row does —
 * what it is to the org, and how connected it is — and pressing it moves the
 * panel there, which is how a reader walks a network without going back to the
 * list for every step.
 */
function TouchRow({
  entry,
  vertical,
  onSelect,
}: {
  entry: KnowledgeEntry;
  vertical: string;
  onSelect: (entityId: string) => void;
}) {
  return (
    <button
      type="button"
      className="knowledge-touch"
      aria-label={knowledgeOpenLabel(entry.display_name)}
      onClick={() => onSelect(entry.entity_id)}
    >
      <span className="knowledge-touch-name">{entry.display_name}</span>
      <span className="knowledge-meta">
        {KNOWLEDGE_KIND_WORDS[entry.kind]} · <KnowledgeStanding entry={entry} vertical={vertical} />
        {entry.account ? null : ` · ${knowledgeConnections(entry.connections)}`}
      </span>
    </button>
  );
}

/**
 * One fact, in the record's own words: the line the dossier writes, over the
 * dating and corroboration the dossier writes under it.
 *
 * Read-only, unlike the record's `FactRow` — no Correct, no claims disclosure,
 * no queued answer. A removed fact still lists, greyed and labelled, because
 * removed things stay visible everywhere in this system (`@ni/shared` §2).
 */
/**
 * **The fact's sentence, written for whoever this panel is centred on.**
 *
 * The record's builders leave the reader implicit and name the far side, which
 * is right on the two centres they were written for: the fact's own subject, and
 * the person on the other column of a pair fact. An **organisation's** panel is
 * neither. Its facts are the org-context types — employment, board seats,
 * memberships — whose sentence names `fields.org` and ignores the reader, so a
 * club's panel read "Blue Harbour, Chief executive" twelve times over and never
 * once said whose employment it was.
 *
 * **The centre being off the fact cannot be the test.** The panel's read is
 * `subject_entity_id = :id OR counterparty_entity_id = :id`, so the centre is in
 * one of the two columns of every fact it is handed — and an org reached by one
 * of those types is resolved into the *counterparty* slot, never off the row. A
 * test on the columns therefore never fired, and the club's panel still read
 * twelve unattributed lines.
 *
 * So the test is the **kind** and the **subject column**: on an organisation's
 * or an event's panel, a fact whose subject is somebody else is written in the
 * subject's direction and the subject named ahead of it. A person's panel is
 * left alone — the sentence is already about them, and naming them would be the
 * app telling a reader who they are looking at — and so is a fact the centre is
 * itself the subject of, which the org-subject types (an event participation the
 * club hosted, a recapitalization keyed to it) produce.
 *
 * Done here rather than in `fact-copy.ts` deliberately: the dossier's sentence is
 * the dossier's, and this is a second reader with a different question.
 */
export function panelFactLine(fact: Fact, entry: KnowledgeEntry): string {
  const centre = { id: entry.entity_id, name: entry.display_name };
  if (entry.kind === 'person') return factLine(fact, centre);
  /* `sameEntityId`, not `===`: the columns are what Postgres rendered a `uuid`
     as, and the centre's id came off the address (`pair-side.ts`). */
  if (sameEntityId(fact.subject_entity_id, entry.entity_id)) return factLine(fact, centre);

  const subject = fact.subject_display_name;
  /* A shredded subject keeps the row and loses the name (`@ni/shared` §8). The
     unattributed sentence is then all there is, and it is still true. */
  if (subject === null) return factLine(fact, centre);
  return knowledgeFactSubject(
    subject,
    factLine(fact, { id: fact.subject_entity_id, name: subject }),
  );
}

function PanelFact({ fact, entry }: { fact: Fact; entry: KnowledgeEntry }) {
  const off = fact.status !== 'active';
  const source = joinClauses([factDating(fact), corroborationClause(fact)]);
  const offLabel = off
    ? fact.negation_reason
      ? NEGATION_LABELS[fact.negation_reason]
      : 'no longer current'
    : null;

  return (
    <div className={off ? 'cat-item off' : 'cat-item'}>
      <div className="cat-line">
        {/* The sentence is written about whatever the panel is about, which is
            what makes a pair fact read the right way round on either side of
            it — the id decides, never the name (`fact-copy.ts`). */}
        {panelFactLine(fact, entry)}
        {offLabel ? <span className="tag-faint">{offLabel}</span> : null}
        {fact.sensitivity === 'sensitive' ? <span className="tag-faint">sensitive</span> : null}
      </div>
      {source.length > 0 ? <div className="src">{source}</div> : null}
    </div>
  );
}
